import { SuggestionDataItem } from 'react-mentions';
import { combine, createEffect, createEvent, createStore, forward, merge, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { hideAiHelperEvent, setAiOpenOptionsEvent, showAiHelperEvent } from '@nx-frontend/shared/store/ai-helper';
import { $isFreePlan } from '@nx-frontend/shared/store/org-info';
import { AgentThreadInput, AiMentionType } from '@nx-frontend/shared/types';
import {
  AiMentionsResponse,
  AiMentionsStore,
  ChatbotCitation,
  ChatbotCitationDocument,
  ChatbotMessage,
  ChatbotMessageContextChunkContent,
  ChatbotMessageRole,
  ChatbotStore,
  ChatbotStoredThread,
  ChatbotThread,
  ChatbotThreadMessageRaw,
  ChatBotToolCallContent,
  ChatbotUsage,
  MainPageResponse,
  MessageChunk,
  MessageChunkType,
  PromptSuggestion,
  PromptType,
  SystemMessageTypes,
  UserLocationType,
} from './types';
import { createMessage, mapToAiThreadMessages, mapToMentionList, SYSTEM_THREAD_ID } from './util';

const AI_HELPER_SIZE_STORAGE_KEY = 'fusebase-ai-helper-size';
const AI_HELPER_OPEN_STATS_LOCAL_STORAGE_KEY = 'fusebase-ai-helper-open-stats';
const AI_HELPER_FEEDBACK_GIVEN_STORAGE_KEY = 'fusebase-ai-helper-feedback-given';
const AI_HELPER_UI_POSITION_STORAGE_KEY = 'fusebase-ai-helper-ui-position';
const AI_HELPER_PORTAL_VISITOR_STORAGE_KEY = 'fusebase-ai-helper-portal-visitor';

interface AiHelperOpenStats {
  /**
   * Number of times the AI helper was opened (it is tracked only once per browser tab session)
   */
  opens: number;
  /**
   * Whether the AI helper was opened in the current browser tab session
   */
  openTrackedInCurrentSession: boolean;
  /**
   * Timestamp of the last time the AI helper was opened
   */
  lastOpenTimestampMs: number;
}

interface AIUI {
  /**
   * Position offsets related to the default position (bottom right corner)
   */
  x: number;
  y: number;

  shaking?: boolean;
}

const aiHelperOpenStatsInitialState: AiHelperOpenStats = {
  opens: 0,
  openTrackedInCurrentSession: false,
  lastOpenTimestampMs: 0,
};

export const $aiHelperOpenStats = createStore<AiHelperOpenStats>(
  JSON.parse(
    (typeof window !== 'undefined' &&
      window.localStorage &&
      localStorage.getItem(AI_HELPER_OPEN_STATS_LOCAL_STORAGE_KEY)) ||
      'null'
  ) || aiHelperOpenStatsInitialState
);

export const $aiPortalVisitorDetails = createStore<{
  portalVisitorId: string;
} | null>(null);

export const initPortalVisitorDetailsEvent = createEvent();

$aiPortalVisitorDetails.on(initPortalVisitorDetailsEvent, () => {
  let portalVisitorId = localStorage.getItem(AI_HELPER_PORTAL_VISITOR_STORAGE_KEY);
  if (!portalVisitorId) {
    // generate portalVisitorId if it doesn't exist
    portalVisitorId = Math.random().toString(36).substring(2);
    localStorage.setItem(AI_HELPER_PORTAL_VISITOR_STORAGE_KEY, portalVisitorId);
  }
  return { portalVisitorId };
});

/* AI all threads store */
export const $aiThreads = createStore<ChatbotStore>({
  threads: {},
  preferences: {
    searchEnabled: false,
    logoStoredFileUUID: null,
    name: null,
  },
  size: { width: 708, height: 664 },
  behaviourFeedbackDone: false,
});
export const $writingMessage = createStore<
  | (ChatbotMessage & { threadId: string | null; isTextCompleted: boolean; activeToolCall?: ChatBotToolCallContent })
  | null
>(null);

/* Active threadId units */
export const $activeThreadId = createStore<string | null>(null);
export const setActiveThreadIdEvent = createEvent<string | null>();
export const updateThreadEvent = createEvent<{
  id: string;
  thread: Partial<ChatbotStoredThread>;
}>();

export const $assistantFeedback = createStore({
  given:
    typeof window !== 'undefined' &&
    window.localStorage &&
    localStorage.getItem(AI_HELPER_FEEDBACK_GIVEN_STORAGE_KEY) === '1',
});
export const giveAssistantFeedbackEvent = createEvent();

interface UserPilotSurveyEvent {
  survey_id: number;
  event: string;
  act: string;
}

let latestUserPilotSurveyCallback: (event: unknown) => void;
const giveAssistantFeedbackFx = createEffect(() => {
  return new Promise((resolve, reject) => {
    if (window.userpilot) {
      window.userpilot.trigger('survey:uQIqj7lw5_');

      if (latestUserPilotSurveyCallback) {
        window.userpilot.off('survey_completed', latestUserPilotSurveyCallback);
      }
      latestUserPilotSurveyCallback = (event: unknown) => {
        if (!event) {
          return;
        }
        const eventSurvey: UserPilotSurveyEvent = event as UserPilotSurveyEvent;
        if (eventSurvey.survey_id === 12 && eventSurvey.act === 'completed' && eventSurvey.event === 'events:survey') {
          resolve(true);
        }
      };

      window.userpilot.on('survey_completed', latestUserPilotSurveyCallback);
    } else {
      console.info('Userpilot is not initialized to open AI Assistant feedback survey');
    }
  });
});

$assistantFeedback.on(giveAssistantFeedbackFx.done, (store) => {
  localStorage.setItem(AI_HELPER_FEEDBACK_GIVEN_STORAGE_KEY, '1');
  return { ...store, given: true };
});

sample({
  source: $assistantFeedback,
  clock: giveAssistantFeedbackEvent,
  fn: (store) => {
    return store;
  },
  target: giveAssistantFeedbackFx,
});

/* Clear dislike from messages on exit from thread */
sample({
  source: { store: $aiThreads, threadId: $activeThreadId },
  clock: setActiveThreadIdEvent,
  fn: ({ store, threadId }) => {
    if (threadId) {
      const { dislikeMessages, ...thread } = store.threads[threadId];
      return {
        ...store,
        threads: {
          ...store.threads,
          [threadId]: thread,
        },
      };
    }
    return store;
  },
  filter: (_, payload) => payload === null,
  target: $aiThreads,
});
sample({ clock: setActiveThreadIdEvent, target: $activeThreadId });

export const $editMessage = combine($aiThreads, $activeThreadId).map(([aiThreads, activeThreadId]) => {
  if (!activeThreadId || !aiThreads) {
    return null;
  }
  const thread = aiThreads.threads[activeThreadId];

  if (!thread || !thread.editMessageId) {
    return null;
  }

  const editMessageId = thread.editMessageId;

  return thread.messages?.find((message: ChatbotMessage) => message.id === editMessageId);
});

interface SendMessageParams {
  threadId: string | null;
  sessionId?: string | null;
  apiHost?: string;
  workspaceId: string;
  portalId?: string;
  portalVisitorId?: string;
  text: string;
  onMessageChunkResolved: (payload: MessageChunk) => void;
  abortController: AbortController;
  promptType: PromptType;
  useWeb: boolean;
  currentLocationType: UserLocationType;
  currentLocationId?: string | null;
  currentLocationTitle?: string | null;
  enableDetectUserMessageContext?: boolean;
}

/* Writing session units */
export const sendMessageEvent = createEvent<SendMessageParams>();
export const setEditMessageIdEvent = createEvent<{ threadId: string; messageId: string | null }>();

export const abortMessageEvent = createEvent<{
  sessionId?: string | null;
  apiHost: string;
  workspaceId: string;
  messageId: string;
  text: string;
}>();
const $abortController = createStore<AbortController | null>(null).on(abortMessageEvent, (abortController) => {
  if (abortController !== null) {
    abortController.abort();
  }
  return null;
});

const showSurveyForFrequentUserIfNeededFx = createEffect(
  async ({
    stats,
    aiThreads,
    apiHost,
    sessionId,
    workspaceId,
  }: {
    stats: AiHelperOpenStats;
    aiThreads: ChatbotStore;
    apiHost: string;
    sessionId?: string | null;
    workspaceId: string;
  }) => {
    stats = stats as AiHelperOpenStats;
    aiThreads = aiThreads as ChatbotStore;
    if (stats.opens >= 5 && !aiThreads.behaviourFeedbackDone) {
      if (window.userpilot) {
        window.userpilot.trigger('survey:A1hh1hY2XK');
      } else {
        console.info('Userpilot is not initialized to open AI Assistant feedback survey for a frequent user');
      }

      // mark on the server that behaviour feedback was done
      await axiosClient.post(
        `/ai-assistant/rest/workspaces/${workspaceId}/done-feedback`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'everhelper-session-id': sessionId,
          },
        }
      );

      return true;
    }
    return false;
  }
);

const showSurveyForInfrequentUserIfNeededFx = createEffect(
  async ({
    stats,
    aiThreads,
    apiHost,
    sessionId,
    workspaceId,
  }: {
    stats: AiHelperOpenStats;
    aiThreads: ChatbotStore;
    apiHost: string;
    sessionId?: string | null;
    workspaceId: string;
  }) => {
    stats = stats as AiHelperOpenStats;
    aiThreads = aiThreads as ChatbotStore;
    if (
      stats.opens >= 1 &&
      stats.opens <= 4 &&
      !aiThreads.behaviourFeedbackDone &&
      stats.lastOpenTimestampMs &&
      (Date.now() - stats.lastOpenTimestampMs) / 24 / 3600 / 1000 >= 7
    ) {
      await new Promise((resolve) => {
        setTimeout(resolve, 20e3);
      });

      if (window.userpilot) {
        window.userpilot.trigger('survey:JmzXS5N_8a');
      } else {
        console.info('Userpilot is not initialized to open AI Assistant feedback survey for an infrequent user');
      }

      // mark on the server that behaviour feedback was done
      await axiosClient.post(
        `/ai-assistant/rest/workspaces/${workspaceId}/done-feedback`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'everhelper-session-id': sessionId,
          },
        }
      );

      return true;
    }
    return false;
  }
);

export const onMessageResolvedEvent = createEvent<MessageChunk>();
export const writeMessageFx = createEffect(
  async ({
    threadId,
    sessionId,
    apiHost,
    workspaceId,
    portalId,
    portalVisitorId,
    text,
    onMessageChunkResolved,
    abortController,
    promptType,
    useWeb,
    currentLocationType,
    currentLocationId,
    currentLocationTitle,
    enableDetectUserMessageContext,
  }: SendMessageParams) => {
    try {
      for await (const chunk of createMessage({
        sessionId,
        apiHost,
        workspaceId,
        portalId,
        portalVisitorId,
        threadId,
        text,
        signal: abortController.signal,
        promptType,
        useWeb,
        currentLocationType,
        currentLocationId,
        currentLocationTitle,
        enableDetectUserMessageContext,
      })) {
        onMessageChunkResolved(chunk);
        if (chunk.type === MessageChunkType.DONE) {
          break;
        }
      }
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        onMessageChunkResolved({ type: MessageChunkType.DONE, content: 'aborted' } as MessageChunk);
      } else {
        throw error;
      }
    }
  }
);

/* On stop writing message fx */
const stopMessageFx = createEffect(
  async ({
    sessionId,
    apiHost,
    workspaceId,
    messageId,
    text,
  }: {
    sessionId?: string | null;
    apiHost: string;
    workspaceId: string;
    messageId: string;
    text: string;
  }) => {
    try {
      await axiosClient.post(
        `https://${apiHost}/ai-assistant/rest/workspaces/${workspaceId}/messages/${messageId}/delete`,
        { text },
        {
          headers: {
            'Content-Type': 'application/json',
            'everhelper-session-id': sessionId,
          },
        }
      );
    } catch (err) {
      return null;
    }
  }
);
forward({ from: abortMessageEvent, to: stopMessageFx });

/* On send message event add user message to aiThreads store */
$aiThreads.on(sendMessageEvent, (state, { threadId, text }) => {
  const messages = [
    {
      role: ChatbotMessageRole.USER,
      message: text,
    },
  ];
  if (threadId && state.threads && state.threads[threadId]) {
    const threadMessages = (
      typeof state.threads[threadId].messages !== 'undefined' && state.threads[threadId]?.messages?.length
        ? state.threads[threadId].messages
        : []
    ) as ChatbotMessage[];
    threadMessages.push(...messages);

    return Object.assign({}, state, {
      threads: {
        ...state.threads,
        [threadId]: Object.assign({}, state.threads[threadId], {
          messages: threadMessages,
          isWriting: true,
          updatedAt: new Date().getTime(),
        }),
      },
    });
  }
  return { ...state, tmpMessages: messages };
});

$aiThreads.on(setEditMessageIdEvent, (state, { threadId, messageId }) => {
  return Object.assign({}, state, {
    threads: {
      ...state.threads,
      [threadId]: Object.assign({}, state.threads[threadId], {
        editMessageId: messageId,
      }),
    },
  });
});

/* On send message event create empty message in writingMessage store */
$writingMessage.on(sendMessageEvent, (_, payload) => {
  const message = {
    role: ChatbotMessageRole.AI,
    message: '',
    threadId: payload.threadId ?? null,
    isTextCompleted: false,
  };
  return message;
});
/* On send message event init abort controller store */
sample({
  clock: sendMessageEvent,
  fn: ({ abortController }) => {
    return abortController;
  },
  target: $abortController,
});
/* On send messge event call writeMessageFx effect */
sample({
  clock: sendMessageEvent,
  fn: (payload) => {
    return payload;
  },
  target: writeMessageFx,
});
/* On writing message save new thread to store, add suggestions, update usage */
sample({
  source: { aiThreads: $aiThreads, activeThreadId: $activeThreadId, writingMessage: $writingMessage },
  clock: onMessageResolvedEvent,
  fn: (store, payload): ChatbotStore => {
    const { aiThreads, activeThreadId, writingMessage } = store;
    if (payload.type === MessageChunkType.THREAD) {
      const content = payload.content as ChatbotThread & { id: string };
      if ('id' in content) {
        const { id, ...threadInfo } = content;
        if (!aiThreads.threads[id]) {
          const messages =
            aiThreads.tmpMessages !== undefined && aiThreads.tmpMessages.length ? aiThreads.tmpMessages : [];
          return {
            ...aiThreads,
            threads: {
              ...aiThreads.threads,
              [id]: { ...threadInfo, messages, isWriting: true },
            },
            tmpMessages: undefined,
          };
        }
      } else if ('title' in content) {
        const writingThreadId = activeThreadId || writingMessage?.threadId;
        if (writingThreadId && aiThreads.threads[writingThreadId]) {
          return {
            ...aiThreads,
            threads: {
              ...aiThreads.threads,
              [writingThreadId]: {
                ...aiThreads.threads[writingThreadId],
                title: (payload.content as ChatbotThread).title,
              },
            },
          };
        }
      }
    } else if (payload.type === MessageChunkType.SUGGESTIONS) {
      if (activeThreadId && aiThreads.threads[activeThreadId]) {
        return {
          ...aiThreads,
          threads: {
            ...aiThreads.threads,
            [activeThreadId]: {
              ...aiThreads.threads[activeThreadId],
              suggestions: payload.content as string[],
            },
          },
        };
      }
    } else if (payload.type === MessageChunkType.USAGE) {
      return {
        ...aiThreads,
        usage: payload.content as ChatbotUsage,
      };
    } else if (payload.type === MessageChunkType.MESSAGE_CONTEXT) {
      const messageContext = (payload.content as ChatbotMessageContextChunkContent).contextDetails;
      // assign message context to the last message in the thread
      const threadId = activeThreadId || writingMessage?.threadId;
      if (threadId && aiThreads.threads[threadId]) {
        const messages = aiThreads.threads[threadId].messages;
        if (messages.length) {
          const lastMessage = messages[messages.length - 1];
          if (lastMessage.role === ChatbotMessageRole.USER) {
            const lastMessageCopy = { ...lastMessage };
            lastMessageCopy.messageContext = messageContext;

            return {
              ...aiThreads,
              threads: {
                ...aiThreads.threads,
                [threadId]: {
                  ...aiThreads.threads[threadId],
                  messages: [...messages.slice(0, -1), lastMessageCopy],
                },
              },
            };
          }
        }
      } else if (aiThreads.tmpMessages && aiThreads.tmpMessages.length) {
        const lastMessageCopy = { ...aiThreads.tmpMessages[aiThreads.tmpMessages.length - 1] };
        lastMessageCopy.messageContext = messageContext;
        return {
          ...aiThreads,
          tmpMessages: [...aiThreads.tmpMessages.slice(0, -1), lastMessageCopy],
        };
      }
      return {
        ...aiThreads,
      };
    } else if (payload.type === MessageChunkType.REQUEST_MESSAGE) {
      const content = payload.content as ChatbotThread & { id: string };
      if ('id' in content) {
        const { id } = content;
        const threadId = activeThreadId || writingMessage?.threadId;
        if (threadId) {
          const thread = store.aiThreads.threads[threadId];
          const messages = thread.messages;
          const lastMessage = messages[messages.length - 1];
          if (lastMessage.role === ChatbotMessageRole.USER && !lastMessage.id) {
            const updatedMessage = { ...lastMessage, id };
            return {
              ...aiThreads,
              threads: {
                ...aiThreads.threads,
                [threadId]: {
                  ...aiThreads.threads[threadId],
                  messages: [...messages.slice(0, -1), updatedMessage],
                },
              },
            };
          }
        }
      }
    }
    return aiThreads;
  },
  filter: (_, payload) => {
    return [
      MessageChunkType.THREAD,
      MessageChunkType.SUGGESTIONS,
      MessageChunkType.USAGE,
      MessageChunkType.MESSAGE_CONTEXT,
      MessageChunkType.REQUEST_MESSAGE,
    ].includes(payload.type);
  },
  target: $aiThreads,
});
/* On writing message save message to writing store */
sample({
  source: { writingMessage: $writingMessage, isFreePlan: $isFreePlan },
  clock: onMessageResolvedEvent,
  fn: ({ writingMessage: store, isFreePlan }, payload) => {
    if (!store) return store;
    if (payload.type === MessageChunkType.THREAD) {
      const content = payload.content as ChatbotThread & { id: string };
      if ('id' in content) {
        const { id } = content;
        return { ...store, threadId: id };
      }
    } else if (payload.type === MessageChunkType.TEXT) {
      return { ...store, message: store.message + payload.content };
    } else if (payload.type === MessageChunkType.CITATIONS) {
      return { ...store, citations: payload.content as ChatbotCitation[] };
    } else if (payload.type === MessageChunkType.DOCS) {
      return { ...store, documents: payload.content as ChatbotCitationDocument[] };
    } else if (payload.type === MessageChunkType.MSG_INFO) {
      const { id } = payload.content as { id: string };
      return { ...store, id };
    } else if (payload.type === MessageChunkType.REQUEST_MESSAGE) {
      const { id } = payload.content as { id: string };
      return { ...store, id };
    } else if (payload.type === MessageChunkType.LIMIT_ERROR) {
      const content = payload.content as ChatbotThread & { id: string };
      let threadId = SYSTEM_THREAD_ID;
      if ('id' in content) {
        threadId = content.id;
      }
      return { ...store, message: SystemMessageTypes.LIMIT_REACHED, system: true, threadId, isTextCompleted: true };
    } else if (payload.type === MessageChunkType.TEXT_STREAM_END) {
      return { ...store, isTextCompleted: true };
    } else if (payload.type === MessageChunkType.TOOL_CALL) {
      return {
        ...store,
        activeToolCall: payload.content as ChatBotToolCallContent,
      };
    } else if (payload.type === MessageChunkType.REASONING) {
      return {
        ...store,
        activeReasoning: {
          content: payload.content as string,
        },
      };
    }
    return store;
  },
  filter: (store, payload) => {
    return (
      store !== null &&
      [
        MessageChunkType.THREAD,
        MessageChunkType.TEXT,
        MessageChunkType.CITATIONS,
        MessageChunkType.DOCS,
        MessageChunkType.MSG_INFO,
        MessageChunkType.REQUEST_MESSAGE,
        MessageChunkType.LIMIT_ERROR,
        MessageChunkType.TEXT_STREAM_END,
        MessageChunkType.TOOL_CALL,
        MessageChunkType.REASONING,
      ].includes(payload.type)
    );
  },
  target: $writingMessage,
});
/* On end writing message copy ai message to aiThreads store */
sample({
  source: { aiThreads: $aiThreads, writingMessage: $writingMessage },
  clock: onMessageResolvedEvent,
  fn: ({ aiThreads, writingMessage }) => {
    if (writingMessage !== null) {
      const { threadId, isTextCompleted, ...message } = writingMessage;
      if (threadId) {
        return {
          ...aiThreads,
          threads: {
            ...aiThreads.threads,
            ...{
              [threadId]: {
                ...aiThreads.threads[threadId],
                messages: [...aiThreads.threads[threadId].messages, message],
                isWriting: false,
                updatedAt: new Date().getTime(),
              },
            },
          },
        };
      }
    }
    return aiThreads;
  },
  filter: ({ writingMessage }, payload) => {
    return writingMessage !== null && payload.type === MessageChunkType.DONE;
  },
  target: $aiThreads,
});
/* On end writing message reset writingMessage store */
sample({
  clock: onMessageResolvedEvent,
  fn: () => {
    return null;
  },
  filter: (payload) => {
    return payload.type === MessageChunkType.DONE;
  },
  target: $writingMessage,
});
/* On create new thread on writing message set active thread id */
sample({
  source: $activeThreadId,
  clock: onMessageResolvedEvent,
  fn: (activeThreadId, payload) => {
    if (!activeThreadId) {
      const content = payload.content as ChatbotThread;
      if ('id' in content) {
        return content.id as string;
      }
    }
    return activeThreadId;
  },
  filter: (_, payload) => payload.type === MessageChunkType.THREAD,
  target: $activeThreadId,
});

/* Main page units */
export const $aiMainPagePrompts = createStore<PromptSuggestion[]>([]);
const $isMainPageRequested = createStore(false);

export const $aiUIStore = createStore<AIUI>(
  JSON.parse(
    (typeof window !== 'undefined' && window.localStorage && localStorage.getItem(AI_HELPER_UI_POSITION_STORAGE_KEY)) ||
      'null'
  ) || {
    x: -Infinity,
    y: -Infinity,
  }
);

export const setAIUIPositionEvent = createEvent<AIUI>();

$aiUIStore.on(setAIUIPositionEvent, (store, payload) => {
  localStorage.setItem(AI_HELPER_UI_POSITION_STORAGE_KEY, JSON.stringify(payload));
  return Object.assign({}, store, {
    x: payload.x,
    y: payload.y,
  });
});

const fetchAiMainPageFx = createEffect(
  async ({
    workspaceId,
    portalId,
    portalVisitorId,
    apiHost,
    sessionId,
  }: {
    workspaceId: string;
    portalId?: string;
    portalVisitorId?: string;
    apiHost: string;
    sessionId?: string | null;
    userId: number; // нужен для установки userId в thread, так как запрос не возвращает в thread ид юзера
  }): Promise<MainPageResponse & { size: { width: number; height: number } }> => {
    let url = `/ai-assistant/rest/workspaces/${workspaceId}/main-page`;
    const query: string[] = [];
    if (portalId) {
      query.push(`portalId=${encodeURIComponent(portalId)}`);
    }
    if (portalVisitorId) {
      query.push(`portalVisitorId=${encodeURIComponent(portalVisitorId)}`);
    }
    if (query.length) {
      url += '?' + query.join('&');
    }
    const response = await axiosClient.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'everhelper-session-id': sessionId,
      },
    });
    const savedSize = localStorage.getItem(AI_HELPER_SIZE_STORAGE_KEY);
    const size = savedSize ? JSON.parse(savedSize) : { width: 708, height: 664 };
    return { ...response.data, size };
  }
);

export const updateAiMainPageEvent = createEvent<{
  workspaceId: string;
  portalId?: string;
  portalVisitorId?: string;
  apiHost: string;
  sessionId?: string | null;
  userId: number;
}>();

sample({
  source: $isMainPageRequested,
  clock: updateAiMainPageEvent,
  fn: (_, payload) => payload,
  filter: (isFetched, _) => !isFetched,
  target: fetchAiMainPageFx,
});

$isMainPageRequested.on(fetchAiMainPageFx.pending, () => true);

$aiThreads.on(fetchAiMainPageFx.done, (store, { params, result }) => {
  const existedThreadsIds = Object.keys(store.threads);
  const { lastThreads, usage, preferences, size, behaviourFeedbackDone } = result;
  const threadsToStore = lastThreads.filter((thread) => !existedThreadsIds.includes(thread.id));
  const newThreads = threadsToStore.reduce(
    (threads, thread) => {
      const { id: threadId, ...threadInfo } = thread;
      threads[threadId] = {
        ...threadInfo,
        workspaceId: params.workspaceId,
        userId: params.userId,
        isWriting: false,
        messages: [],
      };
      return threads;
    },
    {} as {
      [threadId: string]: ChatbotThread & {
        messages: ChatbotMessage[];
        isWriting: boolean;
      };
    }
  );

  return { ...store, threads: { ...store.threads, ...newThreads }, usage, preferences, size, behaviourFeedbackDone };
});

$aiMainPagePrompts.on(fetchAiMainPageFx.doneData, (_, { promptSuggestions }) => {
  return promptSuggestions;
});

$aiThreads.on(updateThreadEvent, (store, data) => {
  const { id, thread } = data;
  return {
    ...store,
    threads: {
      ...store.threads,
      [id]: {
        ...(store.threads[id] || {}),
        ...thread,
      },
    },
  };
  return store;
});

/* History units */
export const updateAiWorkspaceThreadsEvent = createEvent<{
  workspaceId: string;
  portalId?: string;
  apiHost: string;
  sessionId?: string | null;
  portalVisitorId?: string;
}>();
export const updateAiThreadMessagesEvent = createEvent<{
  workspaceId: string;
  portalId?: string;
  portalVisitorId?: string;
  apiHost: string;
  sessionId?: string | null;
  threadId: string;
}>();
export const updateAiAgentThreadMessagesEvent = createEvent<{
  apiHost: string;
  sessionId?: string | null;
  threadId: string;
  orgId: string;
}>();

const fetchAiWorkspaceThreadsFx = createEffect(
  async ({
    workspaceId,
    portalId,
    apiHost,
    sessionId,
    portalVisitorId,
  }: {
    workspaceId: string;
    portalId?: string;
    apiHost: string;
    sessionId?: string | null;
    portalVisitorId?: string;
  }): Promise<(ChatbotThread & { id: string })[]> => {
    let url = `/ai-assistant/rest/workspaces/${workspaceId}/threads`;
    const query: string[] = [];
    if (portalId) {
      query.push(`portalId=${encodeURIComponent(portalId)}`);
    }
    if (portalVisitorId) {
      query.push(`portalVisitorId=${encodeURIComponent(portalVisitorId)}`);
    }
    url += '?' + query.join('&');
    const response = await axiosClient.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'everhelper-session-id': sessionId,
      },
    });
    return response.data;
  }
);
forward({ from: updateAiWorkspaceThreadsEvent, to: fetchAiWorkspaceThreadsFx });

const fetchAiThreadMessagesFx = createEffect(
  async ({
    workspaceId,
    apiHost,
    sessionId,
    threadId,
    portalId,
    portalVisitorId,
  }: {
    workspaceId: string;
    apiHost: string;
    sessionId?: string | null;
    threadId: string;
    portalId?: string;
    portalVisitorId?: string;
  }): Promise<ChatbotThreadMessageRaw[]> => {
    let url = `/ai-assistant/rest/workspaces/${workspaceId}/threads/${threadId}/messages`;
    const queryArgs = [];

    if (portalId) {
      queryArgs.push(`portalId=${encodeURIComponent(portalId)}`);
    }
    if (portalVisitorId) {
      queryArgs.push(`portalVisitorId=${encodeURIComponent(portalVisitorId)}`);
    }

    if (queryArgs.length) {
      url += '?' + queryArgs.join('&');
    }

    const response = await axiosClient.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'everhelper-session-id': sessionId,
      },
    });
    return response.data;
  }
);
forward({ from: updateAiThreadMessagesEvent, to: fetchAiThreadMessagesFx });

const fetchAiAgentThreadMessagesFx = createEffect(
  async ({
    apiHost,
    sessionId,
    threadId,
    orgId,
  }: {
    apiHost: string;
    sessionId?: string | null;
    threadId: string;
    portalId?: string;
    portalVisitorId?: string;
    orgId: string;
  }): Promise<{
    input: AgentThreadInput;
    messages: ChatbotThreadMessageRaw[];
  }> => {
    const url = `/ai-assistant/rest/org/${orgId}/threads/${threadId}/agent-messages?input=true`;

    const response = await axiosClient.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'everhelper-session-id': sessionId,
      },
    });
    return response.data;
  }
);
forward({ from: updateAiAgentThreadMessagesEvent, to: fetchAiAgentThreadMessagesFx });

$aiThreads.on(fetchAiWorkspaceThreadsFx.doneData, (store, threads) => {
  const existedThreadsIds = Object.keys(store.threads);
  const threadsToWrite = threads.filter((thread) => !existedThreadsIds.includes(thread.id));
  if (threadsToWrite.length) {
    const newThreads = threadsToWrite.reduce(
      (threads, thread) => {
        const { id: threadId, ...threadInfo } = thread;
        threads[threadId] = {
          ...threadInfo,
          workspaceId: thread.workspaceId,
          userId: thread.userId,
          isWriting: false,
          messages: [],
        };
        return threads;
      },
      {} as {
        [threadId: string]: ChatbotThread & {
          messages: ChatbotMessage[];
          isWriting: boolean;
        };
      }
    );
    return { ...store, threads: { ...store.threads, ...newThreads } };
  }
  return store;
});

$aiThreads.on(fetchAiThreadMessagesFx.done, (store, { params, result }) => {
  if (!store.threads[params.threadId]) return store;
  const messages = mapToAiThreadMessages(result);
  return {
    ...store,
    threads: { ...store.threads, [params.threadId]: { ...store.threads[params.threadId], messages } },
  };
});

$aiThreads.on(fetchAiAgentThreadMessagesFx.done, (store, { params, result }) => {
  const messages = mapToAiThreadMessages(result.messages);
  const input = result.input;
  return {
    ...store,
    threads: { ...store.threads, [params.threadId]: { ...store.threads[params.threadId], messages, input } },
  };
});

/* Delete thread units */
export const deleteAiThreadEvent = createEvent<{
  workspaceId: string;
  apiHost: string;
  sessionId?: string | null;
  threadId: string;
  portalVisitorId?: string;
}>();
export const $isDeletingAiThreadId = createStore<string | null>(null); // microstore for threads

const deleteAiThreadFx = createEffect(
  async ({
    workspaceId,
    apiHost,
    sessionId,
    threadId,
    portalVisitorId,
  }: {
    workspaceId: string;
    apiHost: string;
    sessionId?: string | null;
    threadId: string;
    portalVisitorId?: string;
  }) => {
    let url = `/ai-assistant/rest/workspaces/${workspaceId}/threads/${threadId}`;
    const query: string[] = [];
    if (portalVisitorId) {
      query.push(`portalVisitorId=${encodeURIComponent(portalVisitorId)}`);
    }
    if (query.length) {
      url += '?' + query.join('&');
    }
    const response = await axiosClient.delete(url, {
      headers: {
        'Content-Type': 'application/json',
        'everhelper-session-id': sessionId,
      },
    });

    if (response.data.name !== 'NotFound' && response.data.name !== 'AccessDenied') {
      return true;
    }
    return false;
  }
);

forward({ from: deleteAiThreadEvent, to: deleteAiThreadFx });
$isDeletingAiThreadId.on(deleteAiThreadEvent, (_, { threadId }) => threadId);
$isDeletingAiThreadId.on(deleteAiThreadFx.finally, () => null);
$aiThreads.on(deleteAiThreadFx.done, (store, { params, result }) => {
  if (!result) return store;
  const withoutDeletedThreadsIds = Object.keys(store.threads).filter((threadId) => threadId !== params.threadId);
  return {
    ...store,
    threads: withoutDeletedThreadsIds.reduce((threads, threadId) => {
      threads[threadId] = store.threads[threadId];
      return threads;
    }, {} as { [threadId: string]: ChatbotStoredThread }),
  };
});

/* Add to page units */
export const aiAddMessageToPageEvent = createEvent<{
  threadId: string;
  sessionId?: string | null;
  apiHost: string;
  workspaceId: string;
  text: string;
}>();

const aiAddMessageToPageFx = createEffect(
  async ({
    sessionId,
    apiHost,
    workspaceId,
    text,
  }: {
    threadId: string;
    sessionId?: string | null;
    apiHost: string;
    workspaceId: string;
    text: string;
  }) => {
    const response = await axiosClient.post(
      `/ai-assistant/rest/workspaces/${workspaceId}/create-page`,
      { text },
      {
        headers: {
          'Content-Type': 'application/json',
          'everhelper-session-id': sessionId,
        },
      }
    );
    return response.data;
  }
);

forward({ from: aiAddMessageToPageEvent, to: aiAddMessageToPageFx });

$aiThreads.on(aiAddMessageToPageEvent, (store, { threadId }) => {
  if (threadId && store.threads && store.threads[threadId]) {
    return Object.assign({}, store, {
      threads: {
        ...store.threads,
        [threadId]: Object.assign({}, store.threads[threadId], {
          isWriting: true,
        }),
      },
    });
  }
  return store;
});

$writingMessage
  .on(aiAddMessageToPageEvent, (_, { threadId }) => {
    return {
      role: ChatbotMessageRole.AI,
      message: '',
      system: true,
      threadId,
      isTextCompleted: false,
    };
  })
  .reset(aiAddMessageToPageFx.done);

$aiThreads.on(aiAddMessageToPageFx.done, (store, { params, result }) => {
  const { threadId } = params;
  const domain = new URL(window.location.href).origin;
  if (threadId && store.threads[threadId]) {
    return {
      ...store,
      ...{
        threads: {
          ...store.threads,
          ...{
            [threadId]: {
              ...store.threads[threadId],
              messages: [
                ...store.threads[threadId].messages,
                {
                  role: ChatbotMessageRole.AI,
                  message: `Your new page :a[${result.title}]{data-link-role="note" data-global-id="${result.globalId}" data-workspace-id="${params.workspaceId}" data-type="mention"} is ready. It is currently in Unsorted folder and can be accessed via a separate link:\n\n:a[${domain}/space/${params.workspaceId}/page/${result.globalId}]{data-link-role="note" data-global-id="${result.globalId}" data-workspace-id="${params.workspaceId}"}`,
                  system: true,
                },
              ],
              isWriting: false,
            },
          },
        },
      },
    };
  }
  return store;
});

/* Enable/disable workspace indexing units */
export const updateWorkspaceIndexingEvent = createEvent<{
  sessionId?: string | null;
  apiHost: string;
  workspaceId: string;
  searchEnabled: boolean;
}>();

const updateWorkspaceIndexingFx = createEffect(
  async ({
    sessionId,
    apiHost,
    workspaceId,
    searchEnabled,
  }: {
    sessionId?: string | null;
    apiHost: string;
    workspaceId: string;
    searchEnabled: boolean;
  }): Promise<{ search_enabled: boolean }> => {
    const response = await axiosClient.post(
      `https://${apiHost}/ai-assistant/rest/workspaces/${workspaceId}/preferences`,
      { searchEnabled },
      {
        headers: {
          'Content-Type': 'application/json',
          'everhelper-session-id': sessionId,
        },
      }
    );
    return response.data;
  }
);

forward({ from: updateWorkspaceIndexingEvent, to: updateWorkspaceIndexingFx });

$aiThreads.on(updateWorkspaceIndexingEvent, (store, { searchEnabled }) => {
  return { ...store, preferences: { ...store.preferences, searchEnabled } };
});

$aiThreads.on(updateWorkspaceIndexingFx.doneData, (store, { search_enabled }) => {
  return { ...store, preferences: { ...store.preferences, searchEnabled: search_enabled } };
});

$aiThreads.on(updateWorkspaceIndexingFx.failData, (store) => {
  return { ...store, preferences: { ...store.preferences, searchEnabled: !store.preferences.searchEnabled } };
});

/* Resize chatbot units */
export const setAiHelperSizeEvent = createEvent<{ width?: number; height?: number }>();

const setAiHelperWidthFx = createEffect((size: { width: number; height: number }) => {
  localStorage.setItem(AI_HELPER_SIZE_STORAGE_KEY, JSON.stringify(size));
  return size;
});

sample({
  source: $aiThreads,
  clock: setAiHelperSizeEvent,
  fn: (store, payload) => {
    const { width, height } = store.size;
    return {
      width: payload.width ?? width,
      height: payload.height ?? height,
    };
  },
  target: setAiHelperWidthFx,
});

$aiThreads.on(setAiHelperWidthFx.doneData, (store, size) => {
  return { ...store, size };
});

/* Feedback units */
export const markMessageDislikeEvent = createEvent<{ threadId: string; messageId: string }>();
/* Mark message dislike */
$aiThreads.on(markMessageDislikeEvent, (store, { threadId, messageId }) => {
  if (store.threads[threadId] && !store.threads[threadId].dislikeMessages?.includes(messageId)) {
    const dislikeMessages: string[] = store.threads[threadId].dislikeMessages ?? [];
    return {
      ...store,
      threads: {
        ...store.threads,
        [threadId]: {
          ...store.threads[threadId],
          dislikeMessages: [...dislikeMessages, messageId],
        },
      },
    };
  }
  return store;
});

/* Mentions units */
interface FetchMentionsParams {
  apiHost: string;
  workspaceId: string;
  sessionId?: string | null;
  portalId?: string;
  type?: AiMentionType;
  query: string;
  limit?: number;
  offset?: number;
  /**
   * if true fetches only mentions from portal
   */
  onlyPortal?: boolean;
}

export const $aiMentions = createStore<AiMentionsStore>({
  items: [],
  isFetching: false,
  isLazyFetching: false,
  total: 0,
});
export const fetchMentionsEvent = createEvent<FetchMentionsParams>();
export const clearMentionsEvent = createEvent();
$aiMentions.reset(clearMentionsEvent);

async function fetchMentionsFn({
  apiHost,
  workspaceId,
  sessionId,
  portalId,
  type,
  query = '',
  limit,
  offset,
  onlyPortal,
}: FetchMentionsParams) {
  const mentions: (SuggestionDataItem & {
    type: string;
  })[] = [];
  let total: Partial<Record<keyof AiMentionsResponse, number>> | number = 0;
  if (!query && !type && !onlyPortal) {
    mentions.push({
      type: 'workspace',
      id: `data-type="workspace" data-global-id="${workspaceId}"`,
      display: 'All in my internal space',
    });
  }
  try {
    const response = await axiosClient.get<AiMentionsResponse>(
      `/ai-assistant/rest/workspaces/${workspaceId}/mentions`,
      {
        params: {
          portalId,
          type,
          query,
          limit,
          offset,
          onlyPortal: onlyPortal ? 'true' : 'false',
        },
        headers: {
          'Content-Type': 'application/json',
          'everhelper-session-id': sessionId,
        },
      }
    );
    if (response.data) {
      mentions.push(...mapToMentionList(response.data));
      if (type && type in response.data) {
        total = response.data[type].total;
      } else {
        total = Object.keys(response.data).reduce((res, mentionType) => {
          const cat = mentionType as keyof AiMentionsResponse;
          return {
            ...res,
            [cat]: response.data[cat].total,
          };
        }, {});
      }
    }
  } catch (err) {
    console.error(err);
  }
  if (portalId && !query && !type) {
    mentions.push({
      type: 'portal',
      id: `data-type="portal" data-global-id="${portalId}"`,
      display: 'All in portal',
    });
  }
  return {
    mentions: mentions as SuggestionDataItem[],
    total,
  };
}

let TID: NodeJS.Timeout | number | null = null;
const fetchMentionsFx = createEffect(
  async (
    params: FetchMentionsParams
  ): Promise<{
    mentions: SuggestionDataItem[];
    total: Partial<Record<keyof AiMentionsResponse, number>> | number;
  }> => {
    TID && clearTimeout(TID);
    return new Promise((resolve) => {
      TID = setTimeout(() => {
        resolve(fetchMentionsFn(params));
      }, 500);
    });
  }
);
forward({ from: fetchMentionsEvent, to: fetchMentionsFx });

$aiMentions.on(fetchMentionsEvent, (store, { query, type, offset }) => {
  return {
    items: !offset ? [] : store.items,
    isFetching: !offset,
    query,
    showAll: type && Object.values(AiMentionType).includes(type as AiMentionType) ? (type as AiMentionType) : undefined,
    isLazyFetching: !!offset,
    total: 0,
  };
});

$aiMentions.on(fetchMentionsFx.done, (store, { params, result }) => {
  const priority = ['workspace', 'folder', 'note', 'portal', 'portalSection', 'portalPage'];
  const forLazy = !!params.type && !!params.offset;
  const items = [];
  if (forLazy) {
    items.push(...store.items);
  }
  items.push(
    ...result.mentions.sort((a, b) => {
      if (
        priority.indexOf((a as SuggestionDataItem & { type: string }).type) >
        priority.indexOf((b as SuggestionDataItem & { type: string }).type)
      ) {
        return 1;
      } else if (
        priority.indexOf((a as SuggestionDataItem & { type: string }).type) <
        priority.indexOf((b as SuggestionDataItem & { type: string }).type)
      ) {
        return -1;
      } else {
        return 0;
      }
    })
  );
  const state = { ...store, items, isFetching: false, total: result.total };
  if (store.showAll) {
    state.isLazyFetching = false;
  }
  return state;
});

export const clearSystemThreadEvent = createEvent<void>();

$aiThreads.on(clearSystemThreadEvent, (store) => {
  const { system, ...threads } = store.threads;
  return { ...store, threads };
});

const makeAiHelperVisibleEvent = merge([setAiOpenOptionsEvent, showAiHelperEvent]);

$aiHelperOpenStats.on(makeAiHelperVisibleEvent, (state) => {
  if (state.openTrackedInCurrentSession) {
    return state;
  }
  const newState = {
    ...state,
    opens: state.opens + 1,
    openTrackedInCurrentSession: true,
    lastOpenTimestampMs: Date.now(),
  };

  localStorage.setItem(
    AI_HELPER_OPEN_STATS_LOCAL_STORAGE_KEY,
    JSON.stringify({
      ...newState,
      ...{ openTrackedInCurrentSession: false },
    })
  );

  return newState;
});

sample({
  source: [$aiHelperOpenStats, $aiThreads],
  clock: hideAiHelperEvent,
  fn: (sources, clockData) => ({
    stats: sources[0] as AiHelperOpenStats,
    aiThreads: sources[1] as ChatbotStore,
    apiHost: clockData.apiHost,
    sessionId: clockData.sessionId,
    workspaceId: clockData.workspaceId,
  }),
  target: showSurveyForFrequentUserIfNeededFx,
});

$aiThreads.on(showSurveyForFrequentUserIfNeededFx.done, (store, { result }) => {
  if (!result) {
    return store;
  }
  return { ...store, behaviourFeedbackDone: result };
});

$aiThreads.on(showSurveyForInfrequentUserIfNeededFx.done, (store, { result }) => {
  if (!result) {
    return store;
  }
  return { ...store, behaviourFeedbackDone: result };
});

sample({
  source: [$aiHelperOpenStats, $aiThreads],
  clock: fetchAiMainPageFx.done,
  fn: (sources, clockData) => ({
    stats: sources[0] as AiHelperOpenStats,
    aiThreads: sources[1] as ChatbotStore,
    apiHost: clockData.params.apiHost,
    sessionId: clockData.params.sessionId,
    workspaceId: clockData.params.workspaceId,
  }),
  target: showSurveyForInfrequentUserIfNeededFx,
});

export const shakeAiHelperWindowEvent = createEvent();
export const resetShakeEvent = createEvent();

$aiUIStore.on(shakeAiHelperWindowEvent, (state) => {
  return { ...state, shaking: true };
});

$aiUIStore.on(resetShakeEvent, (state) => {
  const newState = { ...state, shaking: false };
  return newState;
});
